<template>
  <layout title="Sign Up">
    <div>
      <div class="sm:mx-auto sm:w-full sm:max-w-md 2xl:pt-20 pt-0">
        <h2
          class="text-3xl font-extrabold leading-9 text-center text-gray-900 mt-3 2xl:mt-6 lg:text-5xl"
        >
          Sign up
        </h2>
        <p class="mt-4 text-sm leading-5 text-center text-gray-600 max-w">
          or, you can
          <inertia-link :href="$route('login')" class="link">
            sign in here
          </inertia-link>
        </p>
      </div>

      <div class="flex flex-col justify-center pb-10 sm:px-6 lg:px-8">
        <div class="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div
            class="px-4 py-8 bg-white border shadow border-gray-50 sm:rounded-lg sm:px-10"
          >
            <form role="form" @submit.prevent="getRecaptchaToken" method="POST">
              <div class="pb-3 sm:border-b sm:border-gray-200">
                <h3 class="text-lg font-medium leading-6 text-gray-900">
                  Profile
                </h3>
                <p class="max-w-2xl mt-1 text-sm leading-5 text-gray-500">
                  Information about your account.
                </p>
              </div>

              <div class="mt-6">
                <label
                  for="name"
                  class="block text-sm font-medium leading-5 text-gray-700 required"
                >
                  Name
                </label>
                <div class="mt-1 rounded-md shadow-sm">
                  <input
                    id="name"
                    type="text"
                    name="name"
                    v-model="form.name"
                    required
                    class="w-full"
                    placeholder="First and Last name"
                  />
                </div>

                <div class="error" v-if="errors.first_name">
                  {{ errors.first_name }}
                </div>
              </div>

              <div class="mt-6">
                <label
                  for="email"
                  class="block text-sm font-medium leading-5 text-gray-700 required"
                >
                  Email Address
                </label>
                <div class="mt-1 rounded-md shadow-sm">
                  <input
                    id="email"
                    type="email"
                    name="email"
                    v-model="form.email"
                    required
                    class="w-full"
                    placeholder="example@mail.com"
                  />
                </div>

                <div class="error" v-if="errors.email">{{ errors.email }}</div>
              </div>

              <div class="mt-6">
                <label
                  for="password"
                  class="block text-sm font-medium leading-5 text-gray-700 required"
                >
                  Password
                </label>
                <div class="mt-1 rounded-md shadow-sm relative">
                  <input
                    id="password"
                    type="password"
                    name="password"
                    v-model="form.password"
                    required
                    class="w-full"
                    placeholder="Password"
                  />
                  <passwordReq inputId="password" @validPass="validPassCheck" />
                </div>

                <div class="error" v-if="errors.password">
                  {{ errors.password }}
                </div>
                <div class="error" v-if="showError">
                  {{ validPass.message }}
                </div>
              </div>

              <div class="mt-6">
                <label
                  for="password_confirmation"
                  class="block text-sm font-medium leading-5 text-gray-700 required"
                >
                  Confirm Password
                </label>
                <div class="mt-1 rounded-md shadow-sm">
                  <input
                    id="password_confirmation"
                    type="password"
                    name="password_confirmation"
                    v-model="form.password_confirmation"
                    required
                    class="w-full"
                    placeholder="Re-enter password"
                  />
                </div>

                <div class="error" v-if="errors.password_confirmation">
                  {{ errors.password_confirmation }}
                </div>
              </div>
              <div class="error" v-if="errors.g_recaptcha_token">
                {{ errors.g_recaptcha_token }}
              </div>

              <div class="mt-6 flex">
                <input
                  id="subscribe_newsletter"
                  name="subscribe_newsletter"
                  type="checkbox"
                  v-model="form.subscribe_newsletter"
                />
                <label
                  for="subscribe_newsletter"
                  class="block ml-2 text-sm leading-5 text-gray-900"
                >
                  Receive our newsletter for exclusive updates
                </label>
              </div>

              <div
                class="flex flex-col items-center justify-center text-sm leading-5"
              >
                <p class="mt-5">
                  By clicking Sign Up, you agree to our
                  <inertia-link :href="$route('termsconditions')" class="link"
                    >Terms</inertia-link
                  >
                  and that you have read our
                  <inertia-link :href="$route('privacypolicy')" class="link"
                    >Privacy Policy</inertia-link
                  >, including our
                  <inertia-link :href="$route('cookiespolicy')" class="link"
                    >Cookie Policy</inertia-link
                  >.
                </p>
                <p class="mt-5">
                  This site is protected by reCAPTCHA and the Google
                  <a href="https://policies.google.com/privacy" class="link"
                    >Privacy Policy</a
                  >
                  and
                  <a href="https://policies.google.com/terms" class="link"
                    >Terms of Service</a
                  >
                  apply.
                </p>

                <span class="block w-full mt-5 rounded-md shadow-sm">
                  <button
                    :sitekey="siteKey"
                    :callback="getRecaptchaToken"
                    data-action="register"
                    class="g-recaptcha btn-primary w-full"
                    type="submit"
                    theme="white"
                  >
                    Sign Up
                  </button>
                </span>
                <div class="mt-3 font-medium">
                  Already have an account?

                  <inertia-link :href="$route('login')" class="link">
                    Sign in here</inertia-link
                  >
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </layout>
</template>

<script>
const layout = () =>
  import(/* webpackChunkName: "layout" */ "../Components/Layout/layout.vue");

const passwordReq = () =>
  import(/* webpackChunkName: "passwordReq" */ "../Components/passwordReq.vue");
export default {
  components: { layout, passwordReq },
  props: {
    errors: Object,
  },
  data() {
    return {
      form: {
        name: null,
        email: null,
        password: null,
        password_confirmation: null,
        subscribe_newsletter: true,
        g_recaptcha_token: null,
        advertising_data: {
          utm_source: null,
          utm_medium: null,
          utm_campaign: null,
        },
      },
      siteKey: process.env.MIX_RECAPTCHA_SITE_KEY,
      validPass: {
        value: false,
        message: "",
      },
      showError: false,
    };
  },
  methods: {
    submit() {
      if (!this.validPass.value) {
        this.showError = true;
        return;
      }
      this.showError = false;
      this.$inertia.post("/register", this.form, {
        onSuccess: (e) => {
          if (e.props.flash.message != null) {
            this.$store.commit("showFlashMessage", {
              message: e.props.flash.message,
              type: e.props.flash.message_type,
            });
          }
          if (e.props.flash.message_type === "success") {
            dataLayer.push({ event: "submit_form_sign_up" });
          }
        },
      });
    },
    getRecaptchaToken() {
      let _this = this;
      grecaptcha
        .execute(this.siteKey, {
          action: "register",
        })
        .then(function (token) {
          _this.form.g_recaptcha_token = token;
          _this.submit();
        });
    },

    getCookieUTM(name) {
      let nameEQ = name + "=";
      let ca = document.cookie.split(";");
      for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === " ") c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0)
          return c.substring(nameEQ.length, c.length);
      }
      return null;
    },

    isFromGoogleAds() {
      const utmFields = ["utm_source", "utm_medium", "utm_campaign"];

      utmFields.forEach((utmField) => {
        let utmValue = this.getCookieUTM(utmField);
        this.form.advertising_data[utmField] = utmValue || "";
      });
    },
    validPassCheck(value) {
      this.validPass = value;
    },
  },
  mounted() {
    this.isFromGoogleAds();
  },
};
</script>